import {
  Box,
  Text,
  VStack,
  HStack,
  Image,
  Flex,
  Heading,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';

export interface ResourcePageStepData {
  number?: string;
  title: string;
  subtitle: string;
  image: string;
  description: string;
  bulletPoints: string[];
  tip?: string;
  note?: string;
  goodToKnow?: string;
  recommendation?: string;
  outcome?: string;
}

interface ResourcePageStepProps {
  step: ResourcePageStepData;
  isLast?: boolean;
  showNumber?: boolean;
}

export const ResourcePageStep: React.FC<ResourcePageStepProps> = ({
  step,
  isLast = false,
  showNumber = true,
}) => {
  return (
    <Box
      bg="white"
      borderRadius="2xl"
      p={6}
      mb={isLast ? 0 : 16}
      position="relative"
      border="1px solid #27AE6020"
      overflow="hidden">
      <Box bg="white" mb={6}>
        <HStack spacing={4} align="start">
          {showNumber && step.number && (
            <Box
              w="50px"
              h="50px"
              borderRadius="full"
              border="1px solid"
              borderColor="gray.300"
              bg="white"
              display="flex"
              alignItems="center"
              justifyContent="center">
              <Text fontSize="16px" fontWeight="bold" color="gray.600">
                {step.number}
              </Text>
            </Box>
          )}
          <VStack align="start" spacing={1}>
            <Heading
              size="21px"
              fontWeight="700"
              color="text.darkBlue.900"
              fontFamily="marketingPage"
              textTransform="uppercase">
              {step.title}
            </Heading>
            <Text color="text.darkGrey" fontSize="15px">
              {step.subtitle}
            </Text>
          </VStack>
        </HStack>
      </Box>
      <Flex direction={{ base: 'column', lg: 'row' }} gap={6}>
        {/* Left side - Image with overlay text */}
        <Box position="relative">
          <Image
            src={step.image}
            alt={`${step.title} screenshot`}
            w="100%"
            borderRadius="16px"
            border="1px solid #F0F0F050"
            maxW={{ base: '100%', md: '480px' }}
            h="auto"
            objectFit="contain"
          />
        </Box>

        {/* Right side - Text content */}
        <Box flex="1" position="relative">
          <VStack align="start" spacing={1} h="full">
            <Text color="text.darkBlue.800" fontSize="15px">
              {step.description}
            </Text>

            <UnorderedList spacing={1} color="text.darkBlue.800" pl={4}>
              {step.bulletPoints.map((point, index) => (
                <ListItem key={index} fontSize="15px">
                  {point.split('**').map((part, partIndex) =>
                    partIndex % 2 === 1 ? (
                      <Text as="span" key={partIndex} fontWeight="bold">
                        {part}
                      </Text>
                    ) : (
                      part
                    ),
                  )}
                </ListItem>
              ))}
            </UnorderedList>

            {step.tip && (
              <Box w="full">
                <Text fontSize="15px">
                  <Text as="span" fontWeight="bold">
                    Tip:
                  </Text>{' '}
                  {step.tip.split('**').map((part, index) =>
                    index % 2 === 1 ? (
                      <Text as="span" key={index} fontWeight="bold">
                        {part}
                      </Text>
                    ) : (
                      part
                    ),
                  )}
                </Text>
              </Box>
            )}

            {step.note && (
              <Box w="full">
                <Text fontSize="15px">
                  <Text as="span" fontWeight="bold">
                    Note:
                  </Text>{' '}
                  {step.note}
                </Text>
              </Box>
            )}

            {step.goodToKnow && (
              <Box w="full">
                <Text fontSize="15px">
                  <Text as="span" fontWeight="bold">
                    Good to know:
                  </Text>{' '}
                  {step.goodToKnow}
                </Text>
              </Box>
            )}

            {step.recommendation && (
              <Box w="full">
                <Text fontSize="15px">
                  <Text as="span" fontWeight="bold">
                    Recommendation:
                  </Text>{' '}
                  {step.recommendation}
                </Text>
              </Box>
            )}

            {step.outcome && (
              <Box w="full">
                <Text fontSize="15px">
                  <Text as="span" fontWeight="bold">
                    Outcome:
                  </Text>{' '}
                  {step.outcome}
                </Text>
              </Box>
            )}
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};
